import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Blocks, Calculator, CheckCircle2, Code2, Flag, Gamepad2, Gift, Globe2, Landmark, Languages, Package, ShoppingCart, Sparkles } from "lucide-react";
import { useOwned, openLink } from "../owned";
import { useCart } from "../context/CartContext";
import { ProductGrid } from "./ProductGrid";
import { Reveal } from "./Reveal";

const BUNDLE_IDS = ["digilib-world-pakiet", "digilib-pakiet-kids"];
const GAME_NAMES = ["Matematyka", "Angielski", "Geografia", "Przyroda", "Historia", "Polski", "Kodowanie"];
// slug wyliczany z id "digilib-world-{slug}" -> etykieta + ikona tematu, do zakladek przedmiotowych
export const SUBJECTS = [
  { slug: "matematyka", label: "Matematyka", icon: Calculator },
  { slug: "angielski", label: "Angielski", icon: Languages },
  { slug: "geografia", label: "Geografia", icon: Globe2 },
  { slug: "przyroda", label: "Przyroda", icon: Blocks },
  { slug: "historia", label: "Historia", icon: Landmark },
  { slug: "polski", label: "Polski", icon: Sparkles },
  { slug: "kodowanie", label: "Kodowanie", icon: Code2 },
];

const fmtZl = (v) => v.toFixed(2).replace(".", ",").replace(",00", "");

// Karta pakietu. Podając `product` (z bazy) cena, cena "przed" i oszczędność liczą się same, a pakiet można od razu kupić.
export function BundleCard({ testid, img, badge, title, desc, items, price, oldPrice, note, tall, soon = true, href, product, badgeCls }) {
  const { add } = useCart();
  const owned = useOwned();
  const hasProduct = !!product;
  const shownPrice = hasProduct ? fmtZl(product.price) : price;
  const shownOld = hasProduct && product.old_price ? `${fmtZl(product.old_price)} zł` : oldPrice;
  const shownNote = hasProduct && product.old_price
    ? `Oszczędzasz ${Math.floor(product.old_price - product.price)} zł względem zakupu osobno`
    : note;
  return (
    <Reveal>
      <div className="glow-border group rounded-3xl p-[2px]" data-testid={testid}>
        <div className={`relative overflow-hidden rounded-3xl bg-slate-950 ${tall ? "min-h-[360px]" : "min-h-[300px]"}`}>
          <img src={img} alt="" aria-hidden="true" loading="lazy" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-60" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/60 to-slate-950/25" />
          <div className="relative flex h-full flex-col justify-between gap-6 p-7 sm:p-10">
            <div>
              <span className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-950 ${badgeCls || "bg-gradient-to-r from-amber-400 to-orange-500"}`}>
                {badge}
              </span>
              <h3 className="mt-4 font-display text-2xl font-extrabold tracking-tight text-white sm:text-3xl">{title}</h3>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-300">{desc}</p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {items.map((it) => (
                  <span key={it} className="rounded-full border border-white/15 bg-slate-950/60 px-3 py-1 text-[11px] font-semibold text-slate-200 backdrop-blur">
                    {it}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-display text-4xl font-extrabold text-white sm:text-5xl">
                  {shownPrice} <span className="text-xl">zł</span>
                  {shownOld && <span className="ml-3 align-middle text-base font-semibold text-slate-400 line-through">{shownOld}</span>}
                </p>
                <p className="mt-1 text-xs font-semibold text-amber-300">{shownNote}</p>
              </div>
              {hasProduct ? (
                <div className="flex flex-wrap items-center gap-2.5">
                  {owned.has(product.id) ? (
                  <a href={openLink(owned.get(product.id))} data-testid={`${testid}-open`}
                    className="flex items-center gap-2 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-emerald-500/30 transition-transform hover:scale-105">
                    <CheckCircle2 className="h-4 w-4" /> Masz już — Otwórz bibliotekę
                  </a>
                  ) : (
                  <button
                    data-testid={`${testid}-add`}
                    onClick={() => add(product)}
                    className="flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-teal-400 px-6 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
                  >
                    <ShoppingCart className="h-4 w-4" /> Dodaj do koszyka
                  </button>
                  )}
                  <Link
                    to={`/produkt/${product.id}`}
                    data-testid={`${testid}-cta`}
                    className="rounded-full border border-white/25 bg-slate-950/40 px-5 py-3 text-sm font-bold text-white backdrop-blur transition-colors hover:bg-white/10"
                  >
                    Co zawiera?
                  </Link>
                </div>
              ) : soon ? (
                <span className="rounded-full border border-violet-400/50 bg-violet-400/15 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-violet-300">
                  Premiera wkrótce
                </span>
              ) : (
                <Link
                  to={href}
                  data-testid={`${testid}-cta`}
                  className="rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 px-6 py-2.5 text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-105 active:scale-95"
                >
                  Zobacz pakiet
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </Reveal>
  );
}

// Sekcja GRY EDUKACYJNE — osobna kategoria obok aplikacji: banner WOW, wielkie pakiety, siatka gier.
export function GamesSection({ games }) {
  const [subject, setSubject] = useState(() => new URLSearchParams(window.location.search).get("gra") || "all"); // ?gra= z menu na innej podstronie
  React.useEffect(() => {
    const h = (e) => setSubject(e.detail);
    window.addEventListener("set-games-subject", h);
    return () => window.removeEventListener("set-games-subject", h);
  }, []);
  if (!games.length) return null;
  const allSingles = games.filter((g) => g.id.startsWith("digilib-world-") && !BUNDLE_IDS.includes(g.id));
  const singles = subject === "all" ? allSingles : allSingles.filter((g) => g.id === `digilib-world-${subject}`);
  const bundleGames = games.find((g) => g.id === "digilib-world-pakiet");
  const bundleKids = games.find((g) => g.id === "digilib-pakiet-kids");

  return (
    <section id="gry" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="games-section">
      <Reveal>
        <div className="glow-border group rounded-3xl p-[2px]">
          <div className="relative overflow-hidden rounded-3xl bg-slate-950">
            <img
              src="/img/banners/digilib-world-campaign.jpeg"
              alt="Świat gier edukacyjnych DigiLib World"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-45 transition-all duration-700 group-hover:scale-105 group-hover:opacity-75 group-hover:saturate-150"
              loading="lazy"
            />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/40 to-slate-950/95" />
            <div className="relative px-6 py-12 text-center sm:px-12">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/40 bg-amber-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-amber-300">
                <Gamepad2 className="h-3.5 w-3.5" /> Seria gier edukacyjnych
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                DigiLib <span className="gradient-text">World</span>
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Wokselowe wyspy pełne zagadek, mostów i skarbów. Dziecko uczy się przez przygodę —
                Ty płacisz raz, a gra zostaje z Wami na zawsze.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                <span className="rounded-full border border-cyan-400/30 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-cyan-200 backdrop-blur" data-testid="games-chip-single">
                  Gra: 49,99 zł
                </span>
                <span className="rounded-full border border-amber-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-amber-200 backdrop-blur" data-testid="games-chip-bundle">
                  Pakiet wszystkich gier: {bundleGames ? fmtZl(bundleGames.price) : 199} zł
                </span>
                <span className="rounded-full border border-violet-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-violet-200 backdrop-blur" data-testid="games-chip-kids">
                  Pakiet KIDS: {bundleKids ? fmtZl(bundleKids.price) : 299} zł
                </span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      {/* WIELKIE PAKIETY — wyróżnione, większe niż zwykłe karty */}
      <div className="mt-10 space-y-6">
        {bundleGames && (
          <BundleCard
            testid="bundle-games-card"
            img="/img/banners/digilib-world-campaign.jpeg"
            badge={<><Package className="h-3.5 w-3.5" /> Pakiet wszystkich gier — najlepsza cena</>}
            title="Wszystkie gry DigiLib World w jednym pakiecie"
            desc="Wszystkie wokselowe światy edukacyjne z Libi i Digim — dziś siedem, a kolejne przedmioty dołączają do pakietu bez dopłat. Kupujesz raz, cała seria jest Twoja na zawsze."
            items={GAME_NAMES}
            product={bundleGames}
            tall
          />
        )}
        {bundleKids && (
          <BundleCard
            testid="bundle-kids-card"
            img="/img/banners/digilib-kids-campaign.jpeg"
            badge={<><Gift className="h-3.5 w-3.5" /> Pakiet KIDS — komplet dla dziecka</>}
            title="Wszystko dla dzieci: DigiKids + gry World i Race"
            desc="Wszystkie aplikacje DigiKids oraz całe serie DigiLib World i DigiLib Race — także gry, które dopiero wychodzą. Jeden pakiet, po zakupie sam wybierasz, w co gra dziecko."
            items={["DigiKids: Matematyka", "DigiKids: Czytanie", "DigiLib World ×7", "DigiLib Race ×7"]}
            product={bundleKids}
            tall
          />
        )}
      </div>

      {/* SIATKA GIER */}
      <Reveal delay={120}>
        <div className="mt-12 flex items-end justify-between">
          <h3 className="font-display text-xl font-bold tracking-tight sm:text-2xl">
          Gry <span className="gradient-text">edukacyjne</span>
          </h3>
          <span className="flex items-center gap-1.5 text-xs text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-amber-400" /> Nowe przedmioty w drodze
          </span>
        </div>
      </Reveal>
      <Reveal delay={160}>
        <div className="mt-5 flex flex-wrap gap-2.5" data-testid="games-subject-tabs">
          <button
            data-testid="games-subject-all"
            onClick={() => setSubject("all")}
            className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${subject === "all" ? "text-slate-950 shadow-lg bg-gradient-to-r from-amber-400 to-orange-500" : "glass-panel text-slate-300 hover:border-amber-400/40"}`}
          >
            <Sparkles className="h-4 w-4" /> Wszystkie
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${subject === "all" ? "bg-slate-950/20" : "bg-slate-800"}`}>{allSingles.length}</span>
          </button>
          {SUBJECTS.map((s) => {
            const Icon = s.icon;
            const isActive = subject === s.slug;
            const count = allSingles.filter((g) => g.id === `digilib-world-${s.slug}`).length;
            if (!count) return null;
            return (
              <button
                key={s.slug}
                data-testid={`games-subject-${s.slug}`}
                onClick={() => setSubject(s.slug)}
                className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${isActive ? "text-slate-950 shadow-lg bg-gradient-to-r from-amber-400 to-orange-500" : "glass-panel text-slate-300 hover:border-amber-400/40"}`}
              >
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </div>
      </Reveal>
      <div key={subject} className="mt-6">
        <ProductGrid products={singles} testid="games-grid" />
      </div>
    </section>
  );
}

// Seria DigiLib RACE (21.10.2026) — wyścigi gokartów z pytaniami, 7 gier przedmiotowych jak DigiLib World.
export function RaceSection({ games }) {
  const [subject, setSubject] = useState(() => new URLSearchParams(window.location.search).get("race") || "all");
  React.useEffect(() => {
    const h = (e) => setSubject(e.detail);
    window.addEventListener("set-race-subject", h);
    return () => window.removeEventListener("set-race-subject", h);
  }, []);
  const all = games.filter((g) => g.id.startsWith("digilib-race-"));
  if (!all.length) return null;
  const singles = subject === "all" ? all : all.filter((g) => g.id === `digilib-race-${subject}`);
  const tab = (active) => `flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${active ? "text-slate-950 shadow-lg bg-gradient-to-r from-pink-400 to-orange-400" : "glass-panel text-slate-300 hover:border-pink-400/40"}`;

  return (
    <section id="race" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="race-section">
      <Reveal>
        <div className="glow-border group rounded-3xl p-[2px]">
          <div className="relative overflow-hidden rounded-3xl bg-slate-950">
            <img src="/img/banners/digilib-race-campaign.jpg" alt="DigiLib Race — wyścigi gokartów z pytaniami"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-50 transition-all duration-700 group-hover:scale-105 group-hover:opacity-80 group-hover:saturate-150" loading="lazy" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/35 to-slate-950/95" />
            <div className="relative px-6 py-12 text-center sm:px-12">
              <span className="inline-flex items-center gap-2 rounded-full border border-pink-400/40 bg-pink-400/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-pink-300">
                <Flag className="h-3.5 w-3.5" /> Nowa seria gier — wyścigi 3D
              </span>
              <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl">
                DigiLib <span className="bg-gradient-to-r from-pink-400 via-orange-300 to-amber-300 bg-clip-text text-transparent">Race</span>
              </h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-slate-300 sm:text-base">
                Gokarty z odrzutowymi skrzydłami, 4 spektakularne światy i bramy z odpowiedziami. Dobra odpowiedź to nitro,
                zła — widowiskowa kraksa. Nauka w pełnym pędzie z robotami Libi i Digi.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
                <span className="rounded-full border border-cyan-400/30 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-cyan-200 backdrop-blur" data-testid="race-chip-single">
                  Gra: {fmtZl(all[0].price)} zł
                </span>
                <span className="rounded-full border border-violet-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-violet-200 backdrop-blur">
                  W pakietach KIDS i MEGA
                </span>
                <span className="rounded-full border border-emerald-400/40 bg-slate-950/60 px-4 py-1.5 text-xs font-bold text-emerald-200 backdrop-blur">
                  Komputer, telefon i aplikacja na Androida
                </span>
              </div>
            </div>
          </div>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="race-subject-tabs">
          <button data-testid="race-subject-all" onClick={() => setSubject("all")} className={tab(subject === "all")}>
            <Sparkles className="h-4 w-4" /> Wszystkie
            <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${subject === "all" ? "bg-slate-950/20" : "bg-slate-800"}`}>{all.length}</span>
          </button>
          {SUBJECTS.map((s) => {
            const Icon = s.icon;
            if (!all.some((g) => g.id === `digilib-race-${s.slug}`)) return null;
            return (
              <button key={s.slug} data-testid={`race-subject-${s.slug}`} onClick={() => setSubject(s.slug)} className={tab(subject === s.slug)}>
                <Icon className="h-4 w-4" /> {s.label}
              </button>
            );
          })}
        </div>
      </Reveal>
      <div key={subject} className="mt-6">
        <ProductGrid products={singles} testid="race-grid" />
      </div>
    </section>
  );
}
